import React, { useState, useEffect } from 'react';
import { X, Upload, Check, AlertCircle, Loader2, Key, Trophy } from 'lucide-react';
import { parseScheduleImage } from '../../services/geminiService';

const ImportScheduleModal = ({ isOpen, onClose, teams, tournaments, onImportGames }) => {
    const [step, setStep] = useState('upload'); // 'upload', 'processing', 'review'
    const [apiKey, setApiKey] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [selectedTournamentId, setSelectedTournamentId] = useState('');
    const [error, setError] = useState(null);
    const [extractedGames, setExtractedGames] = useState([]);

    // Load API Key from environment or local storage on mount
    useEffect(() => {
        const envKey = import.meta.env.VITE_GEMINI_API_KEY;
        if (envKey) {
            setApiKey(envKey);
        } else {
            const storedKey = localStorage.getItem('pitchin_gemini_api_key');
            if (storedKey) setApiKey(storedKey);
        }
    }, []);

    if (!isOpen) return null;

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setImageFile(e.target.files[0]);
            setError(null);
        }
    };

    const handleProcessImage = async () => {
        if (!apiKey) {
            setError('Please enter your Google Gemini API Key');
            return;
        }
        if (!imageFile) {
            setError('Please select an image file');
            return;
        }

        // Save API key only if it's not from env (user entered it)
        if (!import.meta.env.VITE_GEMINI_API_KEY) {
            localStorage.setItem('pitchin_gemini_api_key', apiKey);
        }

        setStep('processing');
        setError(null);

        try {
            const games = await parseScheduleImage(imageFile, apiKey);

            // Post-process to map teams if possible
            const mappedGames = games.map(game => {
                let formattedTime = game.time || '';
                if (formattedTime) {
                    const timeMatch = formattedTime.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM|am|pm)?/);
                    if (timeMatch) {
                        let hours = parseInt(timeMatch[1], 10);
                        const minutes = parseInt(timeMatch[2], 10);
                        const ampm = timeMatch[3];

                        if (ampm) {
                            if (ampm.toUpperCase() === 'PM' && hours < 12) hours += 12;
                            if (ampm.toUpperCase() === 'AM' && hours === 12) hours = 0;
                        }
                        formattedTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
                    }
                }

                return {
                    ...game,
                    time: formattedTime,
                    id: Math.random().toString(36).substr(2, 9), // temp id for list key
                    homeTeamId: teams.find(t => t.name.toLowerCase() === game.homeTeam?.toLowerCase())?.id || '',
                    awayTeamId: teams.find(t => t.name.toLowerCase() === game.awayTeam?.toLowerCase())?.id || '',
                };
            });

            setExtractedGames(mappedGames);
            setStep('review');
        } catch (err) {
            console.error(err);
            setError(err.message || 'Failed to process image. Please try again.');
            setStep('upload');
        }
    };

    const handleGameChange = (id, field, value) => {
        setExtractedGames(prev => prev.map(game => {
            if (game.id !== id) return game;

            const updates = { [field]: value };

            // Auto-update names if IDs change
            if (field === 'homeTeamId') {
                const team = teams.find(t => t.id === value);
                updates.homeTeam = team ? team.name : '';
            }
            if (field === 'awayTeamId') {
                const team = teams.find(t => t.id === value);
                updates.awayTeam = team ? team.name : '';
            }

            return { ...game, ...updates };
        }));
    };

    const handleDeleteGame = (id) => {
        setExtractedGames(prev => prev.filter(g => g.id !== id));
    };

    const handleImport = () => {
        // Filter out games with missing essential info if strictly required, 
        // but we can allow loose imports and let user fix later or just validate here.
        const validGames = extractedGames.filter(g => g.homeTeam && g.awayTeam && g.date);

        if (validGames.length === 0) {
            setError('No valid games to import. Please check Date and Teams.');
            return;
        }

        onImportGames(validGames, selectedTournamentId);
        onClose();
        // Reset state after success
        setStep('upload');
        setImageFile(null);
        setExtractedGames([]);
        setSelectedTournamentId('');
    };

    const isEnvKeyConfigured = !!import.meta.env.VITE_GEMINI_API_KEY;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">

                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b">
                    <h2 className="text-xl font-bold text-gray-800">Import Schedule from Screenshot</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X size={24} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">

                    {error && (
                        <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg flex items-center gap-2">
                            <AlertCircle size={20} />
                            {error}
                        </div>
                    )}

                    {step === 'upload' && (
                        <div className="space-y-6">
                            {/* Tournament Selection */}
                            <div className="bg-purple-50 p-4 rounded-lg">
                                <label className="block text-sm font-semibold text-purple-900 mb-2 flex items-center gap-2">
                                    <Trophy size={16} /> Select Tournament (Optional)
                                </label>
                                <select
                                    value={selectedTournamentId}
                                    onChange={(e) => setSelectedTournamentId(e.target.value)}
                                    className="w-full px-4 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
                                >
                                    <option value="">-- No Tournament (General Games) --</option>
                                    {tournaments && tournaments.map(t => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                                <p className="text-xs text-purple-700 mt-2">
                                    Games will inherit settings (ball type, squad size) from the selected tournament.
                                </p>
                            </div>

                            {/* API Key Section */}
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <label className="block text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                                    <Key size={16} /> Google Gemini API Key
                                </label>
                                {isEnvKeyConfigured ? (
                                    <div className="flex items-center gap-2 text-green-700 bg-green-50 px-3 py-2 rounded border border-green-200">
                                        <Check size={16} />
                                        <span className="text-sm font-medium">Configured via environment variable</span>
                                    </div>
                                ) : (
                                    <>
                                        <input
                                            type="password"
                                            value={apiKey}
                                            onChange={(e) => setApiKey(e.target.value)}
                                            placeholder="Paste API Key here..."
                                            className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        />
                                        <p className="text-xs text-blue-700 mt-2">
                                            Key is stored locally in your browser. Get a free key from <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="underline font-bold">Google AI Studio</a>.
                                        </p>
                                    </>
                                )}
                            </div>

                            {/* Upload Area */}
                            <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-500 transition-colors">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    className="hidden"
                                    id="schedule-upload"
                                />
                                <label htmlFor="schedule-upload" className="cursor-pointer block">
                                    <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                    <p className="text-lg font-medium text-gray-700 mb-1">
                                        {imageFile ? imageFile.name : 'Click to select screenshot'}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                        Upload an image of the game schedule table
                                    </p>
                                </label>
                            </div>

                            {imageFile && (
                                <button
                                    onClick={handleProcessImage}
                                    className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                                >
                                    Process Image
                                </button>
                            )}
                        </div>
                    )}

                    {step === 'processing' && (
                        <div className="text-center py-12">
                            <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900">Analyzing Schedule...</h3>
                            <p className="text-gray-500">Extracting game details using AI</p>
                        </div>
                    )}

                    {step === 'review' && (
                        <div className="space-y-4">
                            <p className="text-sm text-gray-600">
                                Found <strong>{extractedGames.length}</strong> games. Please review and correct details before importing.
                            </p>

                            <div className="overflow-x-auto border rounded-lg">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Home Team</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Away Team</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Venue</th>
                                            <th className="px-3 py-2 w-10"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {extractedGames.map((game, index) => (
                                            <tr key={game.id} className="hover:bg-gray-50">
                                                <td className="px-3 py-2">
                                                    <input
                                                        type="date"
                                                        value={game.date || ''}
                                                        onChange={(e) => handleGameChange(game.id, 'date', e.target.value)}
                                                        className="w-full text-sm border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <input
                                                        type="time"
                                                        value={game.time || ''}
                                                        onChange={(e) => handleGameChange(game.id, 'time', e.target.value)}
                                                        className="w-full text-sm border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <div className="flex flex-col gap-1">
                                                        <select
                                                            value={game.homeTeamId || ''}
                                                            onChange={(e) => handleGameChange(game.id, 'homeTeamId', e.target.value)}
                                                            className="w-full text-sm border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                                        >
                                                            <option value="">Select Team...</option>
                                                            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                                        </select>
                                                        <input
                                                            type="text"
                                                            value={game.homeTeam || ''}
                                                            onChange={(e) => handleGameChange(game.id, 'homeTeam', e.target.value)}
                                                            placeholder="Or type custom name"
                                                            className="w-full text-xs text-gray-500 border-none p-0 focus:ring-0 bg-transparent"
                                                        />
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <div className="flex flex-col gap-1">
                                                        <select
                                                            value={game.awayTeamId || ''}
                                                            onChange={(e) => handleGameChange(game.id, 'awayTeamId', e.target.value)}
                                                            className="w-full text-sm border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                                        >
                                                            <option value="">Select Team...</option>
                                                            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                                        </select>
                                                        <input
                                                            type="text"
                                                            value={game.awayTeam || ''}
                                                            onChange={(e) => handleGameChange(game.id, 'awayTeam', e.target.value)}
                                                            placeholder="Or type custom name"
                                                            className="w-full text-xs text-gray-500 border-none p-0 focus:ring-0 bg-transparent"
                                                        />
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <input
                                                        type="text"
                                                        value={game.venue || ''}
                                                        onChange={(e) => handleGameChange(game.id, 'venue', e.target.value)}
                                                        className="w-full text-sm border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </td>
                                                <td className="px-3 py-2 text-center">
                                                    <button
                                                        onClick={() => handleDeleteGame(game.id)}
                                                        className="text-red-400 hover:text-red-600"
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 border-t bg-gray-50 flex justify-end gap-3 rounded-b-xl">

                    {step === 'review' && (
                        <>
                            <button
                                onClick={() => setStep('upload')}
                                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
                            >
                                Start Over
                            </button>
                            <button
                                onClick={handleImport}
                                className="px-6 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 flex items-center gap-2"
                            >
                                <Check size={18} />
                                Import {extractedGames.length} Games
                            </button>
                        </>
                    )}

                    {step !== 'review' && (
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
                        >
                            Cancel
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ImportScheduleModal;
